import React, { useMemo, useState } from 'react';
import { Dialog, Input, Popup, Toast } from 'antd-mobile';
import UploadImgToCND from '../../Auth/ChangeUser/upload';
import { authApi } from '../../features/auth/api/authApi';

const AVATAR_TYPES = [
    { type: 0, label: '默认头像', description: '个人主页与名单使用' },
    { type: 1, label: '绿衣头像', description: '绿色队伍阵容使用' },
    { type: 2, label: '红衣头像', description: '红色队伍阵容使用' },
    { type: 5, label: '紫衣头像', description: '紫色队伍阵容使用' },
    { type: 3, label: '裁判头像', description: '裁判身份使用' },
    { type: 4, label: '门将头像', description: '门将位置使用' },
];

const IMAGE_STATUS = {
    0: { label: '已通过', className: 'is-approved' },
    1: { label: '待审核', className: 'is-pending' },
    2: { label: '已驳回', className: 'is-rejected' },
    3: { label: '已禁用', className: 'is-disabled' },
};

export default function MemberAvatarManager({
    memberId,
    images,
    onRefresh,
    canAudit = true,
    adminDirect = true,
}) {
    const [auditSavingId, setAuditSavingId] = useState('');
    const [rejectingImage, setRejectingImage] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const imageMap = useMemo(() => {
        const map = new Map();
        (images || []).forEach(item => map.set(Number(item.type), item));
        return map;
    }, [images]);

    const pendingCount = useMemo(
        () => (images || []).filter(item => Number(item.status) === 1).length,
        [images]
    );

    const auditImage = async (image, status, remark = null) => {
        if (!image || auditSavingId) return;
        setAuditSavingId(image.id);
        try {
            const response = await authApi.auditImage({
                memberId,
                imgId: image.id,
                status,
                remark,
            });
            if (response.data?.code !== 200) {
                throw new Error(response.data?.msg || '头像审核失败');
            }
            Toast.show({
                icon: 'success',
                content: status === 0 ? '头像已审核通过' : status === 3 ? '头像已移除' : '头像已驳回',
            });
            setRejectingImage(null);
            setRejectReason('');
            await onRefresh?.();
        } catch (error) {
            Toast.show({
                icon: 'fail',
                content: error?.response?.data?.msg || error?.message || '头像审核失败',
            });
        } finally {
            setAuditSavingId('');
        }
    };

    const confirmReject = () => {
        const reason = rejectReason.trim();
        if (!reason) {
            Toast.show({ icon: 'fail', content: '请填写驳回原因' });
            return;
        }
        auditImage(rejectingImage, 2, reason);
    };

    const confirmRemove = image => {
        Dialog.confirm({
            title: '移除当前头像',
            content: '移除后该类型会恢复为默认展示，确定继续吗？',
            confirmText: '确认移除',
            cancelText: '取消',
            onConfirm: () => auditImage(image, 3),
        });
    };

    return (
        <>
            <section className="admin-member-form-card admin-avatar-section">
                <header className="admin-member-section-title admin-avatar-section-title">
                    <div>
                        <strong>头像资料</strong>
                        <span>
                            {adminDirect ? '点击头像可裁剪并重新上传，管理员上传后直接生效' : '点击头像可裁剪并重新上传，提交后需管理员审核'}
                        </span>
                    </div>
                    {pendingCount > 0 && <i>{pendingCount} 张待审核</i>}
                </header>

                <div className="admin-avatar-grid">
                    {AVATAR_TYPES.map(option => {
                        const image = imageMap.get(option.type);
                        const status = image ? IMAGE_STATUS[Number(image.status)] : null;
                        const isSaving = auditSavingId === image?.id;
                        return (
                            <article
                                key={option.type}
                                className={`admin-avatar-card ${Number(image?.status) === 1 ? 'is-pending' : ''}`}
                            >
                                <div className="admin-avatar-card-main">
                                    <UploadImgToCND
                                        memberInfo={memberId}
                                        type={option.type}
                                        adminDirect={adminDirect}
                                        value={image?.image ? [{ url: image.image }] : []}
                                        onChange={() => onRefresh?.()}
                                    />
                                    <div className="admin-avatar-card-copy">
                                        <strong>{option.label}</strong>
                                        <small>{option.description}</small>
                                        {status ? (
                                            <span className={`admin-avatar-status ${status.className}`}>{status.label}</span>
                                        ) : (
                                            <span className="admin-avatar-status is-empty">未上传</span>
                                        )}
                                    </div>
                                </div>

                                {canAudit && Number(image?.status) === 1 && (
                                    <div className="admin-avatar-audit-actions">
                                        <button
                                            type="button"
                                            disabled={isSaving}
                                            onClick={() => auditImage(image, 0)}
                                        >通过</button>
                                        <button
                                            type="button"
                                            disabled={isSaving}
                                            onClick={() => {
                                                setRejectingImage(image);
                                                setRejectReason('');
                                            }}
                                        >驳回</button>
                                    </div>
                                )}

                                {canAudit && image && Number(image.status) !== 1 && (
                                    /* 保留原审核页的头像移除能力，避免视觉重构影响既有管理功能。 */
                                    <button
                                        type="button"
                                        className="admin-avatar-remove"
                                        disabled={isSaving}
                                        onClick={() => confirmRemove(image)}
                                    >移除当前头像</button>
                                )}

                                {image?.remark && (
                                    <div className="admin-avatar-remark">驳回原因：{image.remark}</div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* 驳回使用单层底部面板，避免在资料页继续嵌套确认弹窗。 */}
            <Popup
                visible={Boolean(rejectingImage)}
                position="bottom"
                bodyClassName="admin-avatar-reject-popup"
                onMaskClick={() => !auditSavingId && setRejectingImage(null)}
                destroyOnClose
            >
                <div className="admin-avatar-reject-sheet">
                    <header>
                        <button type="button" onClick={() => setRejectingImage(null)}>取消</button>
                        <strong>驳回头像</strong>
                        <button type="button" disabled={Boolean(auditSavingId)} onClick={confirmReject}>
                            {auditSavingId ? '提交中' : '确认驳回'}
                        </button>
                    </header>
                    <div className="admin-avatar-reject-content">
                        <span>请填写原因，球员重新进入头像页面时可以看到。</span>
                        <Input
                            value={rejectReason}
                            maxLength={100}
                            clearable
                            placeholder="例如：头像不清晰，请重新上传"
                            onChange={setRejectReason}
                        />
                    </div>
                </div>
            </Popup>
        </>
    );
}
